import Vue from "vue";
import VueRouter from "vue-router";

if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}
import HomeView from "../views/HomeView.vue";
import ParticipationValideeView from "../views/ParticipationValideeView.vue";
import FooterPlaceholderView from "../views/FooterPlaceholderView.vue";
import {
  MENTIONS_LEGALES_URL,
  POLITIQUE_CONFIDENTIALITE_URL,
} from "@/constants/reglement";
import {
  isOperationEnded,
  isOperationNotStarted,
  isProductionDomain,
  isStatusPageStillAccessible,
  ROUTES_ALLOWED_OUTSIDE_OPERATION,
} from "@/constants/operation";
import {
  hasCompletedQuizVotes,
  syncQuizVotesFromQuery,
} from "@/utils/quizVotesStorage";
import { syncUtmSourceFromQuery } from "@/utils/utmStorage";
import { hasPreinscriptionValideeAccess, hasPerduAccess } from "@/utils/preinscriptionFlow";

const ROUTES_REQUIRING_QUIZ = [
  "merci-pour-vos-votes",
  "form",
  "preinscription-plus-tard",
];

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    name: "home",
    component: HomeView,
  },
  {
    path: "/participation-validee",
    name: "participation-validee",
    component: ParticipationValideeView,
  },
  {
    path: "/fin",
    name: "jeu-fini",
    component: () => import("../views/JeuFiniView.vue"),
  },
  {
    path: "/perdu",
    name: "perdu",
    component: () => import("../views/PerduView.vue"),
  },
  {
    path: "/maintenance",
    name: "maintenance-jeu",
    component: () => import("../views/MaintenanceJeuView.vue"),
  },
  {
    path: "/attente",
    name: "attente-jeu",
    component: () => import("../views/AttenteJeuView.vue"),
  },
  {
    path: "/quizz/1",
    name: "quizz-etape-1",
    component: () => import("../views/QuizzEtape1View.vue"),
  },
  {
    path: "/quizz/2",
    name: "quizz-etape-2",
    component: () => import("../views/QuizzEtape2View.vue"),
  },
  {
    path: "/quizz/3",
    name: "quizz-etape-3",
    component: () => import("../views/QuizzEtape3View.vue"),
  },
  {
    path: "/quizz/4",
    name: "quizz-etape-4",
    component: () => import("../views/QuizzEtape4View.vue"),
  },
  {
    path: "/quizz/5",
    name: "quizz-etape-5",
    component: () => import("../views/QuizzEtape5View.vue"),
  },
  {
    path: "/merci-pour-vos-votes",
    name: "merci-pour-vos-votes",
    component: () => import("../views/MerciPourVosVotesView.vue"),
  },
  {
    path: "/politique-de-confidentialite",
    name: "politique-de-confidentialite",
    component: FooterPlaceholderView,
    beforeEnter() {
      window.open(
        POLITIQUE_CONFIDENTIALITE_URL,
        "_blank",
        "noopener,noreferrer",
      );
    },
  },
  {
    path: "/mentions-legales",
    name: "mentions-legales",
    component: FooterPlaceholderView,
    beforeEnter() {
      window.open(MENTIONS_LEGALES_URL, "_blank", "noopener,noreferrer");
    },
  },
  {
    path: "/preinscription/validee",
    name: "preinscription-validee",
    component: () => import("../views/PreinscriptionValideeView.vue"),
  },
  {
    path: "/preinscription/plus-tard",
    name: "preinscription-plus-tard",
    component: () => import("../views/LaterView.vue"),
  },
  {
    path: "/contact/confirmation",
    name: "contact-confirmation",
    component: () => import("../views/ContactConfirmationView.vue"),
  },
  {
    path: "/coordonnees/enregistrees",
    name: "coordonnees-enregistrees",
    component: () => import("../views/CoordonneesEnregistreesView.vue"),
  },
  {
    path: "/contact",
    name: "contact",
    component: () => import("../views/ContactView.vue"),
  },
  {
    path: "/status/:_id/:uid",
    name: "status",
    component: () => import("../views/GainView.vue"),
  },
  {
    path: "/form",
    name: "form",
    component: () => import("../views/FormView.vue"),
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

router.beforeEach((to, _from, next) => {
  syncUtmSourceFromQuery(to.query);

  if (to.name === "preinscription-validee" && !hasPreinscriptionValideeAccess()) {
    next({ name: "home" });
    return;
  }

  if (to.name === "perdu" && !hasPerduAccess()) {
    next({ name: "home" });
    return;
  }

  if (ROUTES_REQUIRING_QUIZ.includes(to.name)) {
    syncQuizVotesFromQuery(to.query);
    if (!hasCompletedQuizVotes(to.query)) {
      next({ name: "quizz-etape-1" });
      return;
    }
  }

  if (!isProductionDomain()) {
    next();
    return;
  }

  const isAllowedOutsideOperation = ROUTES_ALLOWED_OUTSIDE_OPERATION.includes(
    to.name,
  );
  const isStatusRoute = to.name === "status";

  if (isStatusRoute && isStatusPageStillAccessible()) {
    next();
    return;
  }

  if (isOperationNotStarted()) {
    if (to.name === "attente-jeu") {
      next();
      return;
    }
    if (!isAllowedOutsideOperation) {
      next({ name: "attente-jeu" });
      return;
    }
  } else if (isOperationEnded()) {
    if (to.name === "jeu-fini" || isAllowedOutsideOperation) {
      next();
      return;
    }
    next({ name: "jeu-fini" });
    return;
  } else if (to.name === "attente-jeu" || to.name === "jeu-fini") {
    next({ name: "home" });
    return;
  }

  next();
});

router.afterEach((to, from) => {
  if (from.name && typeof window.otTrackMetaPageEvents === "function") {
    window.otTrackMetaPageEvents();
  }

  Vue.nextTick(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  });
});

export default router;
